import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../utils/firebase"; // Adjust the path as needed
import { useAuth } from "../../utils/AuthContext";
import { Board } from "../../utils/boards"; // Import the Board type
import Footer from "../../components/feature/Footer";

const BoardPage: React.FC = () => {
  // Get the board ID from the URL parameters.
  const { id } = useParams<{ id: string }>();
  const { currentUser } = useAuth();

  const [board, setBoard] = useState<Board | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    // If there's no logged in user or no id in the URL, stop here.
    if (!currentUser || !id) {
      setLoading(false);
      return;
    }

    // Create a reference to the board document located at:
    // /users/{currentUser.uid}/boards/{id}
    const boardRef = doc(db, "users", currentUser.uid, "boards", id);

    getDoc(boardRef)
      .then((docSnapshot) => {
        if (docSnapshot.exists()) {
          setBoard(docSnapshot.data() as Board);
        } else {
          setError("Board not found.");
        }
      })
      .catch((err) => {
        console.error("Error fetching board:", err);
        setError("Failed to fetch board data.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [currentUser, id]);

  if (loading) {
    return (
      <div className="max-w-[100vw]  mx-auto p-4">
        <p>Loading board...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-[100vw]  mx-auto p-4">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  if (!board) {
    return (
      <div className="max-w-[100vw]  mx-auto p-4">
        <p>Board not found.</p>
      </div>
    );
  }

  return (
    <div className="max-w-[100vw]  mx-auto">
      {/* Board Header */}
      <header className="relative h-64 w-full mb-8">

        {/* Overlay for readability */}
        <div className="absolute inset-0 bg-black opacity-40"></div>
        <div className="absolute inset-0 flex flex-col justify-center items-center text-white px-4">
          <h1 className="text-4xl font-bold">
            { /* You can replace the board id with a title if available */ }
            Board {board.title || 'Titre'}
          </h1>
        </div>
      </header>

      {/* Pinterest‑Inspired Board Pictures Layout */}
      <section className="px-4">
        {board.pictures && board.pictures.length > 0 ? (
          <div className="columns-1 sm:columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4">
            {board.pictures.map((picture) => (
              <div key={picture.id} className="break-inside-avoid mb-4">
                <img
                  src={picture.url}
                  alt="Board item"
                  className="w-full rounded-lg object-cover"
                />
                <div className="mt-2 text-sm text-gray-600">
                  Photo by{" "}
                  <a
                    href={picture.reference}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:underline"
                  >
                    source
                  </a>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p>No pictures found in this board.</p>
        )}
      </section>
      <Footer />
    </div>
  );
};

export default BoardPage;
